<template>
  <div class="p-4 pusaka-list-page">
    <router-link
      to="/"
      class="back-icon-btn"
      title="Kembali ke Halaman Utama"
      aria-label="Kembali ke Halaman Utama"
    >
      <span class="back-icon" aria-hidden="true">&larr;</span>
      <span class="back-text">Beranda</span>
    </router-link>

    <h1 class="text-2xl font-bold mb-4">Daftar Pusaka</h1>

    <div class="search-wrap">
      <input
        v-model.trim="searchQuery"
        type="text"
        class="search-input"
        placeholder="Cari nama pusaka atau kategori..."
      >
      <p class="search-meta" v-if="!loading && !error">
        Menampilkan {{ totalVisiblePusaka }} dari {{ totalPusaka }} pusaka
      </p>
    </div>

    <div v-if="loading">Memuat...</div>
    <div v-else-if="error">{{ error }}</div>
    <div v-else>
      <div v-if="filteredGrouped.length" class="category-grid">
        <section v-for="group in filteredGrouped" :key="group.tipe" class="category-card">
          <div class="category-head">
            <h2 class="category-title">{{ group.tipe }}</h2>
            <span class="category-count">{{ group.pusakaList.length }} pusaka</span>
          </div>

          <div class="pusaka-grid">
            <router-link
              v-for="item in group.pusakaList"
              :key="item"
              :to="`/detail/${encodeURIComponent(item)}`"
              class="pusaka-item"
            >
              {{ formatNama(item) }}
            </router-link>
          </div>
        </section>
      </div>

      <div v-else class="empty-state">
        Tidak ada pusaka yang cocok dengan kata kunci "{{ searchQuery }}".
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'PusakaList',
  data() {
    return {
      pusakaList: [],
      searchQuery: '',
      loading: true,
      error: null
    }
  },
  computed: {
    orderedGrouped() {
      const grouped = this.pusakaList.reduce((acc, item) => {
        const tipe = this.getKategoriPusaka(item)

        if (!acc[tipe]) {
          acc[tipe] = []
        }

        acc[tipe].push(item)
        return acc
      }, {})

      return Object.keys(grouped)
        .sort((a, b) => a.localeCompare(b))
        .map(tipe => ({
          tipe,
          pusakaList: grouped[tipe].sort((a, b) => this.formatNama(a).localeCompare(this.formatNama(b)))
        }))
    },
    filteredGrouped() {
      const keyword = this.searchQuery.toLowerCase()

      if (!keyword) {
        return this.orderedGrouped
      }

      return this.orderedGrouped
        .map(group => {
          const matchedByCategory = group.tipe.toLowerCase().includes(keyword)
          const filteredPusaka = matchedByCategory
            ? group.pusakaList
            : group.pusakaList.filter(item => this.formatNama(item).toLowerCase().includes(keyword))

          return {
            ...group,
            pusakaList: filteredPusaka
          }
        })
        .filter(group => group.pusakaList.length > 0)
    },
    totalPusaka() {
      return this.orderedGrouped.reduce((sum, group) => sum + group.pusakaList.length, 0)
    },
    totalVisiblePusaka() {
      return this.filteredGrouped.reduce((sum, group) => sum + group.pusakaList.length, 0)
    }
  },
  methods: {
    getKategoriPusaka(nama) {
      const formatted = this.formatNama(nama)
      return formatted.split(' ')[0] || 'Lainnya'
    },
    formatNama(nama) {
      return nama
        .replace(/_/g, ' ')
        .split(' ')
        .map(kata => kata.charAt(0).toUpperCase() + kata.slice(1))
        .join(' ')
    }
  },
  mounted() {
    const query = `
      PREFIX rdf: <http://www.w3.org/1999/02/22-rdf-syntax-ns#>
      PREFIX wp: <http://ukdw.ac.id/ontologi/wayangpurwa#>
      PREFIX wpo: <http://ontology/wayangpurwa#>

      SELECT ?individu WHERE {
        ?individu rdf:type wpo:Pusaka .
      }
    `

    axios.get('https://app.alunalun.info/fuseki/wayangpurwa/sparql', {
      params: { query, format: 'json' }
    })
    .then(response => {
      const results = response.data.results.bindings
      this.pusakaList = results
        .map(r => r.individu.value.split(/[#/]/).pop())
        .sort((a, b) => a.localeCompare(b))
    })
    .catch(err => {
      this.error = 'Gagal memuat data pusaka.'
      console.error(err)
    })
    .finally(() => {
      this.loading = false
    })
  }
}
</script>

<style scoped>
.p-4 {
  padding: 32px;
}

.pusaka-list-page {
  max-width: 1200px;
  margin: 0 auto;
}

.back-icon-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  padding: 8px 12px;
  border-radius: 999px;
  text-decoration: none;
  color: #1e40af;
  background: linear-gradient(135deg, #dbeafe, #eff6ff);
  border: 1px solid #bfdbfe;
  box-shadow: 0 6px 16px rgba(30, 64, 175, 0.12);
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
}

.back-icon-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 20px rgba(30, 64, 175, 0.16);
  background: linear-gradient(135deg, #bfdbfe, #dbeafe);
}

.back-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 999px;
  background: #1d4ed8;
  color: #ffffff;
  font-weight: 700;
  line-height: 1;
}

.back-text {
  font-size: 14px;
  font-weight: 600;
}

.search-wrap {
  margin-bottom: 20px;
}

.search-input {
  width: 100%;
  padding: 12px 14px;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  font-size: 15px;
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.search-input:focus {
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
}

.search-meta {
  margin-top: 8px;
  color: #475569;
  font-size: 14px;
}

.category-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
}

.category-card {
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 14px;
  background: #ffffff;
}

.category-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 12px;
}

.category-title {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
}

.category-count {
  font-size: 12px;
  color: #334155;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 999px;
  padding: 2px 10px;
}

.pusaka-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 8px;
}

.pusaka-item {
  display: block;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  color: #1d4ed8;
  text-decoration: none;
  background: #f8fafc;
  transition: all 0.2s ease;
}

.pusaka-item:hover {
  color: #1e40af;
  background: #eaf2ff;
  border-color: #bfdbfe;
}

.empty-state {
  border: 1px dashed #cbd5e1;
  border-radius: 10px;
  padding: 20px;
  color: #475569;
  background: #f8fafc;
}

@media (max-width: 640px) {
  .p-4 {
    padding: 20px;
  }
}
</style>
